import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import { useToggle } from '../../hooks/useToggle';
import PageMeta from '../../components/common/PageMeta';
import { learnerListBySponsorId } from '../../services/sponsor';
import type { TLearner } from '../../types/learner';
import { useParams } from 'react-router';
import debounce from 'lodash.debounce';
import Input from '../../components/form/input/InputField';
import { useSimplePagination } from '../../hooks/useSimplePagination';
import CommonTable, { type ColumnConfig } from '../../components/common/Table';
import { PAGINATION_PER_PAGE } from '../../utils/constants';

const columns: ColumnConfig<TLearner>[] = [
  {
    key: 'id',
    header: 'ID',
    render: (item) => item.id,
  },
  {
    key: 'email',
    header: 'Email',
    className: 'max-w-64 truncate',
    render: (item) => <a href={`mailto:${item.email}`}>{item.email}</a>,
  },
  {
    key: 'planId',
    header: 'PlanID',
  },
  {
    key: 'duration',
    header: 'Duration',
  },
  {
    key: 'isUsed',
    header: 'User Registered',
    render: (item) => (item.isUsed ? 'Yes' : 'No'),
  },
  {
    key: 'createdAt',
    header: 'Created At',
    render: (item) => new Date(item.createdAt).toLocaleString(),
  },
];
const Learners = () => {
  const [subscriptions, setSubscriptions] = useState<TLearner[]>([]);
  const { state: isLoading, on: showLoader, off: hideLoader } = useToggle();
  const [query, setQuery] = useState('');

  const { currentPage, totalPages, onPageChange, setTotalPages } =
    useSimplePagination();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const { sponsorId } = useParams() as { sponsorId: string };

  const fetchLearners = async () => {
    if (isLoading) return;
    showLoader();
    const res = await learnerListBySponsorId(sponsorId, {
      params: {
        query,
        page: currentPage,
        limit: PAGINATION_PER_PAGE,
      },
    }).finally(() => {
      hideLoader();
    });
    setSubscriptions(res.rows);
    setTotalPages(res.count, PAGINATION_PER_PAGE);
  };

  const debouncedResults = useMemo(() => {
    return debounce(fetchLearners, 500);
    // eslint-disable-next-line
  }, [query, currentPage]);

  useEffect(() => {
    debouncedResults();
    return () => {
      debouncedResults.cancel();
    };
  }, [debouncedResults]);

  return (
    <div>
      <PageMeta title="Learners" description="List of Learners" />
      <PageBreadcrumb
        pageTitle="Learners"
        mainPageTitle="Sponsors"
        mainPageLink="/sponsors"
        showBackToParentButton
      />
      <div className="flex justify-evenly my-4">
        <div className="w-1/2" />
        <div className="w-1/2">
          <Input
            placeholder="Search by Email or Duration"
            value={query}
            onChange={handleChange}
          />
        </div>
      </div>
      <CommonTable
        columns={columns}
        data={subscriptions}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        showPagination
      />
    </div>
  );
};

export default Learners;
